import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { useToast } from '../toast'
import { memberApi } from 'src/services/members/api'
import { memberQueries } from 'src/services/members/queries'
import { Role } from 'src/services/members/models'
import {
  PERMISSION_GROUPS,
  getChildGroups,
  getParentGroup,
  isTopLevelGroup,
} from './permission-groups'
import type { PermissionGroup } from './permission-groups'
import { cn } from 'src/utils/style'

/**
 * Resolve a plain-language label for a permission codename or module heading,
 * falling back to the group config's hardcoded English when no translation key
 * exists. Codenames are `module.action`; the i18n key nests both segments under
 * `roles.permissionLabels` to avoid i18next's `.` keySeparator clobbering them.
 */
function usePermissionLabel() {
  const { t } = useTranslation()
  return {
    codenameLabel: (codename: string, fallback: string) => {
      const [module, action] = codename.split('.')
      return t(`roles.permissionLabels.${module}.${action}`, fallback)
    },
    moduleLabel: (module: string, fallback: string) =>
      t(`roles.permissionLabels.${module}.title`, fallback),
  }
}

function PermissionGroupRow({
  group,
  selectedPermissions,
  onToggle,
  isChild = false,
}: {
  group: PermissionGroup
  selectedPermissions: Set<string>
  onToggle: (codename: string) => void
  isChild?: boolean
}) {
  const { codenameLabel, moduleLabel } = usePermissionLabel()
  return (
    <div
      className={cn([
        'rounded-md border border-border p-3',
        isChild ? 'bg-surface' : 'bg-surface-hover',
      ])}
      data-testid={`permission-group-${group.module}`}
    >
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {moduleLabel(group.module, group.label)}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {group.access && (
          <label className="flex items-center gap-1.5 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={selectedPermissions.has(group.access.codename)}
              onChange={() => onToggle(group.access!.codename)}
              className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid={`perm-${group.access.codename}`}
            />
            {codenameLabel(group.access.codename, group.access.label)}
          </label>
        )}
        {group.permissions.map((perm) => (
          <label
            key={perm.codename}
            className="flex items-center gap-1.5 text-sm text-text-primary"
          >
            <input
              type="checkbox"
              checked={selectedPermissions.has(perm.codename)}
              onChange={() => onToggle(perm.codename)}
              className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid={`perm-${perm.codename}`}
            />
            {codenameLabel(perm.codename, perm.label)}
          </label>
        ))}
      </div>
    </div>
  )
}

interface RoleFormModalProps {
  isOpen: boolean
  onClose: () => void
  role?: Role | null
}

export const RoleFormModal: React.FC<RoleFormModalProps> = ({ isOpen, onClose, role }) => {
  const isEditing = !!role
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set())
  const [apiError, setApiError] = useState<string | null>(null)

  // All permission groups are always visible (no module-level gating)
  const visibleGroups = PERMISSION_GROUPS

  useEffect(() => {
    if (isOpen) {
      if (role) {
        setName(role.name)
        setDisplayName(role.displayName)
        setSelectedPermissions(new Set(role.permissions))
      } else {
        setName('')
        setDisplayName('')
        setSelectedPermissions(new Set())
      }
      setApiError(null)
    }
  }, [isOpen, role])

  const togglePermission = useCallback(
    (codename: string) => {
      setSelectedPermissions((prev) => {
        const next = new Set(prev)
        if (next.has(codename)) {
          next.delete(codename)
          // If deselecting an access permission, deselect all action permissions for that module
          const group = visibleGroups.find((g) => g.access?.codename === codename)
          if (group) {
            for (const perm of group.permissions) {
              next.delete(perm.codename)
            }
            // If deselecting a parent's .access, cascade to all children
            for (const child of getChildGroups(visibleGroups, group.module)) {
              if (child.access) next.delete(child.access.codename)
              for (const perm of child.permissions) {
                next.delete(perm.codename)
              }
            }
          }
        } else {
          next.add(codename)
          // If selecting an action permission, auto-select the module access permission
          const group = visibleGroups.find((g) =>
            g.permissions.some((p) => p.codename === codename),
          )
          if (group?.access) {
            next.add(group.access.codename)
            // Also cascade to parent if this module is a child
            const parent = getParentGroup(visibleGroups, group.module)
            if (parent?.access) {
              next.add(parent.access.codename)
            }
          }
          // If selecting a child's .access directly, auto-select the parent's .access
          const accessGroup = visibleGroups.find((g) => g.access?.codename === codename)
          if (accessGroup) {
            const parent = getParentGroup(visibleGroups, accessGroup.module)
            if (parent?.access) {
              next.add(parent.access.codename)
            }
          }
        }
        return next
      })
    },
    [visibleGroups],
  )

  const { mutate: createRole, isPending: isCreating } = useMutation({
    mutationFn: () =>
      memberApi.csc.createRole({
        name,
        displayName,
        permissions: Array.from(selectedPermissions),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberQueries.all() })
      toast({
        title: t('roles.roleCreated'),
        description: t('roles.createdDescription', { name: displayName }),
        type: 'success',
      })
      onClose()
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.display_name?.[0] ||
        error?.response?.data?.permissions?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('roles.createError')
      setApiError(message)
    },
  })

  const { mutate: updateRole, isPending: isUpdating } = useMutation({
    mutationFn: () =>
      memberApi.csc.updateRole({
        id: role!.id,
        name,
        displayName,
        permissions: Array.from(selectedPermissions),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberQueries.all() })
      toast({
        title: t('roles.roleUpdated'),
        description: t('roles.updatedDescription', { name: displayName }),
        type: 'success',
      })
      onClose()
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.display_name?.[0] ||
        error?.response?.data?.permissions?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('roles.updateError')
      setApiError(message)
    },
  })

  const isPending = isCreating || isUpdating
  const isValid = name.trim().length > 0 && displayName.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setApiError(null)
    if (!isValid) return
    if (isEditing) {
      updateRole()
    } else {
      createRole()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('roles.editRole') : t('roles.createRole')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Input
              label={t('roles.roleNameSlug')}
              placeholder={t('roles.roleNameSlugPlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isEditing && role?.isDefault}
              data-testid="role-name-input"
            />
          </div>
          <div>
            <Input
              label={t('roles.displayName')}
              placeholder={t('roles.displayNamePlaceholder')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isEditing && role?.isDefault}
              data-testid="role-display-name-input"
            />
          </div>
        </div>

        <div>
          <h4 className="mb-3 text-sm font-medium text-text-primary">{t('roles.permissions')}</h4>
          <div className="space-y-3">
            {visibleGroups.filter(isTopLevelGroup).map((group) => {
              const children = getChildGroups(visibleGroups, group.module)
              return (
                <div key={group.module}>
                  <PermissionGroupRow
                    group={group}
                    selectedPermissions={selectedPermissions}
                    onToggle={togglePermission}
                  />
                  {children.length > 0 && (
                    <div className="ml-4 mt-1 space-y-1">
                      {children.map((child) => (
                        <PermissionGroupRow
                          key={child.module}
                          group={child}
                          selectedPermissions={selectedPermissions}
                          onToggle={togglePermission}
                          isChild
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {apiError && (
          <p className="text-sm text-error" data-testid="role-form-error">
            {apiError}
          </p>
        )}

        <div className="flex justify-end space-x-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isPending}
            disabled={!isValid}
            data-testid="role-form-submit"
          >
            {isEditing ? t('roles.saveChanges') : t('roles.createRole')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
